/**
 * `?showcase=ui` — exercises the real UI components on the real UIScene: dialogue box (portrait
 * placeholder, typewriter, choices, history), letter panel, pause menu / settings / slots, interact
 * prompt, touch controls (?touch=1), title card and chapter-end card. Keyboard is mapped to the
 * abstract bus input here only (the characters builder owns that in the game).
 */
import Phaser from 'phaser';
import { SHOWCASES } from '@/content/ShowcaseScene';
import { GAME_H, GAME_W } from '@/core/config';
import { FixedStepper } from '@/core/clock';
import { bus, Events } from '@/core/events';
import { SceneKeys } from '@/core/scenes/keys';
import { ACTOR_DEFS } from '@/characters/index';
import { attachUi, getUi, type UiComponents } from './index';
import { ensureFonts, getFontStatus } from './fonts';
import { uiText } from './text';
import { Css, Palette, Slices } from './theme';
import { renderTitle, type TitleHandle } from './title/renderTitle';
import { renderChapterEnd, type ChapterEndHandle } from './chapterEnd/renderChapterEnd';

type Demo = 'dialogue' | 'choices' | 'letter' | 'pause' | 'prompt' | 'touch' | 'chapterEnd' | 'title';
const DEMOS: { key: Demo; label: string }[] = [
  { key: 'dialogue', label: '1 对话' }, { key: 'choices', label: '2 选项' }, { key: 'letter', label: '3 信件' }, { key: 'pause', label: '4 暂停' },
  { key: 'prompt', label: '5 提示' }, { key: 'touch', label: '6 触控' }, { key: 'chapterEnd', label: '7 章末' }, { key: 'title', label: '8 标题' },
];

const LETTER = [
  '亲爱的琼：',
  '我已经远在南陆了。上次给你写信的时候是一个月前吧，我们踏上了去南陆的船。是你在祝福我吗？',
  '我们的船在一个月的时间中只遇上一次大的风浪。天啊，你要看见这样大的风浪一定会吓坏的，黑夜中闪电和雨水混在一起。不过我是一个勇敢的男人，在颠簸的甲板上我仍然稳稳地站着。',
];

export interface UiShowcaseApi {
  run(demo: Demo): Promise<void>;
  ui: UiComponents;
  events: string[];
  fonts: ReturnType<typeof getFontStatus>;
  exitSub(): void;
  /** Portrait texture/frame of the current dialogue line (emotion alias verification). */
  portrait(): { key: string; frame?: string } | null;
}

declare global { interface Window { __uiShowcase?: UiShowcaseApi } }

export function registerShowcase(): void {
  SHOWCASES['ui'] = (scene) => { void buildUiShowcase(scene); };
}

async function waitForUiScene(scene: Phaser.Scene): Promise<Phaser.Scene> {
  const ui = scene.scene.get(SceneKeys.UI);
  const st = ui.scene.settings.status;
  if (st >= Phaser.Scenes.RUNNING) return ui;
  await new Promise<void>((resolve) => ui.events.once(Phaser.Scenes.Events.CREATE, () => resolve()));
  return ui;
}

function devPortrait(scene: Phaser.Scene, key: string, color: number): void {
  if (scene.textures.exists(key)) return;
  const g = scene.make.graphics({ x: 0, y: 0 }, false);
  g.fillStyle(Palette.parchmentDark, 1); g.fillRect(0, 0, 96, 96);
  g.fillStyle(color, 1); g.fillCircle(48, 38, 20); g.fillRoundedRect(18, 60, 60, 40, 12);
  g.fillStyle(Palette.ink, 0.8); g.fillRect(40, 34, 4, 4); g.fillRect(52, 34, 4, 4);
  g.generateTexture(key, 96, 96);
  g.destroy();
}

async function buildUiShowcase(scene: Phaser.Scene): Promise<void> {
  await ensureFonts();
  const uiScene = await waitForUiScene(scene);
  const ui = getUi(scene) ?? attachUi(uiScene);
  const root = ui.uiRoot;

  // Dev actor defs + portraits so the name plate and portrait frame resolve.
  ACTOR_DEFS['jiang'] ??= { texture: 'actor-jiang', displayName: '江子潮', portrait: 'portrait-jiang' };
  ACTOR_DEFS['zhao'] ??= { texture: 'actor-zhao', displayName: '赵呈博', portrait: 'portrait-zhao' };
  ACTOR_DEFS['merchant'] ??= { texture: 'actor-merchant', displayName: '亚北商贩' };
  devPortrait(scene, 'portrait-jiang', Palette.teak);
  devPortrait(scene, 'portrait-zhao', Palette.indigo);

  // ---- mini world (dev): sky/sea bands, a pier and a controllable placeholder "player"
  const world = scene.add.container(0, 0);
  world.add(scene.add.rectangle(0, 0, GAME_W, 130, 0x3a3f57).setOrigin(0));
  world.add(scene.add.rectangle(0, 130, GAME_W, GAME_H - 130, 0x1f6f6a).setOrigin(0));
  world.add(scene.add.rectangle(0, 120, GAME_W, 12, 0x2b5f66).setOrigin(0));
  for (let x = 40; x < GAME_W; x += 96) world.add(scene.add.rectangle(x, 150, 60, 10, 0x8a5a32).setOrigin(0));
  world.add(scene.add.rectangle(200, 180, 240, 90, 0xd9c49a).setOrigin(0));
  const player = scene.add.rectangle(320, 220, 20, 28, 0x8ec95a).setOrigin(0.5, 1).setStrokeStyle(1, 0x1e3a1e);
  const marker = scene.add.circle(0, 0, 6, 0xffb347, 0.8).setVisible(false);
  world.add([player, marker]);
  world.add(uiText(scene, 8, GAME_H - 8, 'UI SHOWCASE · DEV ART · Enter 推进 · Shift 快进 · H 记录 · Esc 暂停 · ←→↑↓/WASD 移动 · 1-8 演示', { fontSize: '10px', color: '#dfe6ee' }).setOrigin(0, 1));

  // ---- toolbar
  const toolbar = scene.add.container(0, 0);
  DEMOS.forEach((d, i) => {
    const x = 8 + i * 78;
    const bar = scene.add.nineslice(x, 8, Slices.bar.key, undefined, 72, 20, Slices.bar.l, Slices.bar.r, Slices.bar.t, Slices.bar.b).setOrigin(0).setInteractive({ useHandCursor: true });
    const t = uiText(scene, x + 36, 18, d.label, { fontSize: '12px', color: Css.parchment }).setOrigin(0.5);
    bar.on('pointerdown', () => void api.run(d.key));
    toolbar.add([bar, t]);
  });
  const status = uiText(scene, GAME_W - 8, 34, '', { fontSize: '10px', color: '#dfe6ee' }).setOrigin(1, 0);
  const fonts = getFontStatus();
  status.setText(`字体：${fonts.usingFallback ? `系统回退（缺 ${fonts.missing.length}）` : '已载入 Nantian Sans/Serif/Display'} · 触控：${ui.touchControls.isEnabled() ? '开' : '关'}`);

  // ---- keyboard → abstract input (showcase-only mapping)
  const events: string[] = [];
  const held = new Set<string>();
  const dir = { x: 0 as -1 | 0 | 1, y: 0 as -1 | 0 | 1 };
  const emitDir = () => {
    const x = (held.has('RIGHT') || held.has('D') ? 1 : 0) - (held.has('LEFT') || held.has('A') ? 1 : 0);
    const y = (held.has('DOWN') || held.has('S') ? 1 : 0) - (held.has('UP') || held.has('W') ? 1 : 0);
    if (x !== dir.x || y !== dir.y) { dir.x = x as -1 | 0 | 1; dir.y = y as -1 | 0 | 1; bus.emitTyped(Events.INPUT_DIR, { x: dir.x, y: dir.y, source: 'keyboard' }); }
  };
  const kb = scene.input.keyboard;
  const keyHandlers: [string, () => void][] = [];
  const bind = (name: string, fn: () => void) => { kb?.on(name, fn); keyHandlers.push([name, fn]); };
  for (const k of ['UP', 'DOWN', 'LEFT', 'RIGHT', 'W', 'A', 'S', 'D']) { bind(`keydown-${k}`, () => { held.add(k); emitDir(); }); bind(`keyup-${k}`, () => { held.delete(k); emitDir(); }); }
  for (const k of ['ENTER', 'SPACE', 'Z']) bind(`keydown-${k}`, () => bus.emitTyped(Events.INPUT_CONFIRM, { source: 'keyboard' }));
  bind('keydown-X', () => bus.emitTyped(Events.INPUT_CANCEL, { source: 'keyboard' }));
  bind('keydown-SHIFT', () => bus.emitTyped(Events.INPUT_FAST_TEXT, { held: true }));
  bind('keyup-SHIFT', () => bus.emitTyped(Events.INPUT_FAST_TEXT, { held: false }));
  DEMOS.forEach((d, i) => bind(`keydown-${['ONE', 'TWO', 'THREE', 'FOUR', 'FIVE', 'SIX', 'SEVEN', 'EIGHT'][i]}`, () => { if (!sub) void api.run(d.key); }));
  bind('keydown-BACKSPACE', () => api.exitSub());

  // ---- world reactions to bus input (fixed-step simulation)
  const touchDir = { x: 0, y: 0 };
  const onDir = (p: { x: number; y: number; source: string }) => { events.push(`dir:${p.x},${p.y}:${p.source}`); if (p.source === 'touch') { touchDir.x = p.x; touchDir.y = p.y; } };
  const onTap = (p: { worldX: number; worldY: number }) => {
    events.push(`tap:${p.worldX},${p.worldY}`);
    marker.setPosition(p.worldX, p.worldY).setVisible(true).setScale(0.4);
    scene.tweens.add({ targets: marker, scale: 1.4, alpha: 0, duration: 400, onComplete: () => marker.setVisible(false).setAlpha(0.8).setScale(1) });
  };
  const onConfirm = (p: { source: string }) => events.push(`confirm:${p.source}`);
  const onCancel = (p: { source: string }) => events.push(`cancel:${p.source}`);
  const onMenu = (p: { source: string }) => events.push(`menu:${p.source}`);
  bus.onTyped(Events.INPUT_DIR, onDir); bus.onTyped(Events.INPUT_TAP_WORLD, onTap);
  bus.onTyped(Events.INPUT_CONFIRM, onConfirm); bus.onTyped(Events.INPUT_CANCEL, onCancel); bus.onTyped(Events.INPUT_MENU, onMenu);
  const stepper = new FixedStepper();
  const pos = { x: 320, y: 220 };
  const onUpdate = (_t: number, delta: number) => {
    stepper.tick(delta, (dt) => {
      if (ui.dialogueBox.visible || root.hasModal() || ui.letterPanel.isOpen() || sub) return;
      const dx = dir.x || touchDir.x, dy = dir.y || touchDir.y;
      pos.x = Phaser.Math.Clamp(pos.x + dx * 90 * dt / 1000, 12, GAME_W - 12);
      pos.y = Phaser.Math.Clamp(pos.y + dy * 90 * dt / 1000, 160, GAME_H - 8);
    });
    player.setPosition(Math.round(pos.x), Math.round(pos.y));
  };
  scene.events.on(Phaser.Scenes.Events.UPDATE, onUpdate);

  // ---- demos
  let sub: { handle: { destroy(): void }; kind: 'title' | 'chapterEnd' } | null = null;
  let promptOn = false;
  const box = ui.dialogueBox;
  const say = async (speaker: string | undefined, text: string, emotion?: string) => {
    const line: { speaker?: string; text: string; emotion?: string } = { text };
    if (speaker) line.speaker = speaker;
    if (emotion) line.emotion = emotion;
    await box.show(line, { instant: false });
    box.pushHistory(speaker, text);
    await box.waitForAdvance();
  };
  const demos: Record<Demo, () => Promise<void>> = {
    async dialogue() {
      // Real actor ids + free-form emotions: the shared portrait atlases resolve through the alias chain
      // (curious → surprised, calm → neutral, thought → neutral); dev ids fall back to the dev portraits.
      await say('jiang-zichao', '老师，你在想什么呢？', 'curious');
      await say('zhao-chengbo', '没想什么，走吧，我们要尽早找到旱龟蛋。', 'calm');
      await say(undefined, '绿色码头是位于南陆最北的码头，是通向中原大陆最近的码头。这里的人大多皮肤颜色较深，身材短小，说的语言也很奇怪；也有一些身材高大的人，皮肤白皙。');
      await say('zhao-chengbo', '你注意到没，这里做生意做得红火的人都是这些棕色皮肤、身材矮小的人。', 'thought');
      const choice = await box.showChoices([{ text: '嗨！' }, { text: '（先看看树叶下的货物）' }]);
      if (choice === 0) {
        await say('merchant', '嗨，哥们！要点啥？');
        await say('jiang', '你会说中原话？');
        await say('merchant', '那当然。我们这些做生意的，基本上都会说三种以上的语言呢。');
      } else {
        await say(undefined, '大片的树叶下面，摊位上摆着仙人球、袋鼠皮和鸵鸟蛋，叫卖的青年人满脸快活。');
      }
      await box.hide();
    },
    async choices() {
      await box.show({ speaker: 'jiang', text: '那么，你这里有旱龟蛋吗？！' }, { instant: false });
      box.pushHistory('jiang', '那么，你这里有旱龟蛋吗？！');
      const i = await box.showChoices([{ text: '海龟蛋？' }, { text: '不，是旱龟蛋。旱──龟──蛋。' }, { text: '（摇头走开）' }]);
      await say('merchant', i === 1 ? '旱龟蛋？旱龟？从来没听说！' : i === 0 ? '哦，海龟蛋？现在可不是季节啊……' : '……');
      await box.hide();
    },
    async letter() {
      await ui.letterPanel.open();
      for (const p of LETTER) await ui.letterPanel.addParagraph(p, {});
      ui.letterPanel.highlightLast();
      await box.show({ text: '（Enter 关闭信纸）' }, { instant: true });
      await box.waitForAdvance();
      await box.hide();
      await ui.letterPanel.close();
    },
    async pause() { ui.pauseMenu.open(); },
    async prompt() { promptOn = !promptOn; bus.emitTyped(Events.INTERACT_PROMPT, promptOn ? { label: '查看蜡烛摊' } : null); },
    async touch() { ui.touchControls.setEnabled(!ui.touchControls.isEnabled()); status.setText(`触控：${ui.touchControls.isEnabled() ? '开' : '关'}`); },
    async chapterEnd() {
      api.exitSub();
      world.setVisible(false); toolbar.setVisible(false); status.setVisible(false);
      const handle: ChapterEndHandle = renderChapterEnd(scene, { onSave: () => { events.push('chapterEnd:save'); return true; }, onTitle: () => api.exitSub(), onRestart: () => api.exitSub() });
      sub = { handle, kind: 'chapterEnd' };
    },
    async title() {
      api.exitSub();
      world.setVisible(false); toolbar.setVisible(false); status.setVisible(false);
      const latest = root.ctx?.saves.latest() ?? null;
      const entries = [
        ...(latest ? [{ label: '继续旅程', run: () => { events.push(`title:continue:${latest.slot}`); api.exitSub(); } }] : []),
        { label: '新的旅程', run: () => { events.push('title:new'); api.exitSub(); } },
      ];
      const handle: TitleHandle = renderTitle(scene, { entries, version: 'showcase', onReady: () => events.push('title:ready') });
      sub = { handle, kind: 'title' };
    },
  };

  const api: UiShowcaseApi = {
    ui, events, fonts,
    portrait: () => box.currentPortrait(),
    run: async (demo) => { events.push(`run:${demo}`); try { await demos[demo](); } catch (e) { console.error('[ui showcase] demo failed', demo, e); } },
    exitSub: () => {
      if (!sub) return;
      sub.handle.destroy();
      sub = null;
      scene.cameras.main.setBackgroundColor(0x0b0f14);
      world.setVisible(true); toolbar.setVisible(true); status.setVisible(true);
    },
  };
  window.__uiShowcase = api;

  scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
    for (const [n, fn] of keyHandlers) kb?.off(n, fn);
    scene.events.off(Phaser.Scenes.Events.UPDATE, onUpdate);
    bus.offTyped(Events.INPUT_DIR, onDir); bus.offTyped(Events.INPUT_TAP_WORLD, onTap);
    bus.offTyped(Events.INPUT_CONFIRM, onConfirm); bus.offTyped(Events.INPUT_CANCEL, onCancel); bus.offTyped(Events.INPUT_MENU, onMenu);
    api.exitSub();
    delete window.__uiShowcase;
  });
}
